import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
  Film, Send, User, LogOut, ChevronDown, Truck,
  Sun, Moon, LayoutDashboard
} from 'lucide-react';
import API from '../utils/api';
import useNotifications from '../hooks/useNotifications';
import NotificationBell from './NotificationBell';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const loc = useLocation();
  const [dropOpen, setDropOpen] = useState(false);
  const dropRef = useRef(null);
  // null when nothing is awaiting delivery, otherwise { count, single }:
  // `single` is the submissionId to deep-link to when exactly one film is
  // pending, or null when several are (→ the /deliver list page).
  const [deliverInfo, setDeliverInfo] = useState(null);
  const notifications = useNotifications({ client: API, basePath: '/api/submissions/notifications', enabled: !!user });

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Surface a "Deliver" nav tab whenever a filmmaker has at least one
  // approved film awaiting delivery — otherwise it's easy to lose track of,
  // since the only other entry point is a button buried inside a film card.
  useEffect(() => {
    if (!user) return;
    API.get('/api/submissions/my').then(r => {
      const pending = (r.data.submissions || []).filter(s => s.status === 'approved' && ['requested', 'qc_failed'].includes(s.delivery_status));
      setDeliverInfo(pending.length === 0
        ? null
        : { count: pending.length, single: pending.length === 1 ? pending[0].id : null });
    }).catch(() => setDeliverInfo(null));
  }, [user, loc.pathname]);

  const links = [
    { path: '/dashboard', label: 'My Films', icon: <Film size={14} /> },
    { path: '/submit', label: 'Submit Film', icon: <Send size={14} /> },
  ];

  return (
    <nav className="main-navbar" style={s.nav}>
      <div className="main-navbar-inner" style={s.inner}>
        {/* Logo */}
        <div style={s.brand} onClick={() => navigate('/dashboard')}>
          <img src="/logo.png" alt="Shortflix" style={s.logo}
            onError={e => { e.target.style.display='none'; e.target.nextSibling.style.display='block'; }} />
          <span style={{ display:'none', fontFamily:'Montserrat', fontWeight:900, fontSize:20, color:'var(--pink)', letterSpacing:'-0.5px' }}>shortflix</span>
        </div>

        {/* Links */}
        <div style={s.links}>
          {links.map(({ path, label, icon }) => {
            const active = loc.pathname === path;
            return (
              <button key={path} onClick={() => navigate(path)}
                style={{ ...s.link, ...(active ? s.linkActive : {}) }}>
                {icon}
                <span className="main-navbar-link-label">{label}</span>
                {active && <span style={s.activeDot} />}
              </button>
            );
          })}
          {deliverInfo && (
            <button onClick={() => navigate(deliverInfo.single ? `/deliver/${deliverInfo.single}` : '/deliver')}
              style={{ ...s.link, ...(loc.pathname.startsWith('/deliver') ? s.linkActive : {}), position: 'relative' }}>
              <Truck size={14} />
              <span className="main-navbar-link-label">Deliver</span>
              {deliverInfo.count > 1
                ? <span style={s.deliverCount}>{deliverInfo.count}</span>
                : <span style={s.deliverDot} />}
              {loc.pathname.startsWith('/deliver') && <span style={s.activeDot} />}
            </button>
          )}
        </div>

        {/* Right */}
        <div style={s.right}>
          <NotificationBell {...notifications} onItemClick={() => navigate('/dashboard')} />

          {/* Theme toggle */}
          <button onClick={toggle} style={s.themeBtn} title={theme === 'dark' ? 'Light mode' : 'Dark mode'}>
            {theme === 'dark' ? <Sun size={15} color="var(--text-secondary)" /> : <Moon size={15} color="var(--text-secondary)" />}
          </button>

          {/* Profile dropdown */}
          <div style={{ position: 'relative' }} ref={dropRef}>
            <button onClick={() => setDropOpen(o => !o)} style={s.profileBtn}>
              {user?.avatar_url
                ? <img src={user.avatar_url} alt="" style={s.avatar} />
                : <div style={s.avatarFallback}>{(user?.name || 'U')[0].toUpperCase()}</div>
              }
              <span className="main-navbar-profile-name" style={s.profileName}>{user?.name?.split(' ')[0]}</span>
              <ChevronDown size={13} color="var(--text-muted)"
                style={{ transform: dropOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
            </button>

            {dropOpen && (
              <div style={s.dropdown} className="fade-in">
                <div style={s.dropHeader}>
                  <div style={s.dropName}>{user?.name}</div>
                  <div style={s.dropEmail}>{user?.email}</div>
                </div>
                <hr className="divider" />
                <button style={s.dropItem} onClick={() => { navigate('/profile'); setDropOpen(false); }}>
                  <User size={14} color="var(--text-secondary)" /> Profile Settings
                </button>
                <button style={s.dropItem} onClick={() => { navigate('/guidelines'); setDropOpen(false); }}>
                  <Film size={14} color="var(--text-secondary)" /> Submission Guidelines
                </button>
                <hr className="divider" />
                <button style={{ ...s.dropItem, color: 'var(--red)' }} onClick={() => { logout(); navigate('/'); setDropOpen(false); }}>
                  <LogOut size={14} color="var(--red)" /> Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

const s = {
  nav: { background: 'var(--nav-bg)', backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)', borderBottom: '1.5px solid var(--border)', position: 'sticky', top: 0, zIndex: 100 },
  inner: { maxWidth: 1200, margin: '0 auto', padding: '0 24px', height: 58, display: 'flex', alignItems: 'center', gap: 8 },
  brand: { cursor: 'pointer', flexShrink: 0, marginRight: 12 },
  logo: { height: 30, display: 'block' },
  links: { display: 'flex', gap: 2, flex: 1 },
  link: { background: 'none', color: 'var(--text-secondary)', padding: '6px 14px', borderRadius: 'var(--r-sm)', fontSize: 12, fontWeight: 600, border: 'none', cursor: 'pointer', position: 'relative', transition: 'color 0.15s, background 0.15s', fontFamily: 'var(--font)', display: 'flex', alignItems: 'center', gap: 6, letterSpacing: '0.02em' },
  linkActive: { color: 'var(--pink)', background: 'var(--bg-elevated)', fontWeight: 800 },
  activeDot: { position: 'absolute', bottom: -7, left: 12, right: 12, height: 2, borderRadius: 2, background: 'var(--pink)' },
  deliverDot: { position: 'absolute', top: 3, right: 4, width: 7, height: 7, borderRadius: '50%', background: 'var(--pink)', boxShadow: '0 0 0 2px var(--nav-bg)' },
  deliverCount: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 16, height: 16, padding: '0 4px', borderRadius: 8, background: 'var(--pink)', color: '#fff', fontSize: 10, fontWeight: 800, lineHeight: 1 },
  right: { display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' },
  themeBtn: { width: 32, height: 32, borderRadius: 'var(--r-sm)', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  profileBtn: { display: 'flex', alignItems: 'center', gap: 7, background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 22, padding: '4px 12px 4px 4px', cursor: 'pointer' },
  avatar: { width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' },
  avatarFallback: { width: 26, height: 26, borderRadius: '50%', background: 'var(--pink)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800 },
  profileName: { fontSize: 12, fontWeight: 600, color: 'var(--text)' },
  dropdown: { position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 220, background: 'var(--bg-card)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-lg)', boxShadow: '0 8px 32px rgba(0,0,0,0.2)', overflow: 'hidden', zIndex: 200 },
  dropHeader: { padding: '14px 16px 12px' },
  dropName: { fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 2 },
  dropEmail: { fontSize: 11, color: 'var(--text-muted)' },
  dropItem: { width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', background: 'none', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', textAlign: 'left', transition: 'background 0.15s' },
};
